/*!
 * acm3d.ts — Motor de conversão do resultado do calcularPorta() (Método ACM)
 * em um modelo de cena 3D por camadas (estrutura de alumínio, chapas ACM lado A/B,
 * ferragens), pronto para ser renderizado com three.js / @react-three/fiber.
 *
 * Não altera nem depende de nada do pipeline de desenho 2D existente
 * (acmGenerator.ts) — consome apenas o objeto puro devolvido por calcularPorta().
 */
import { CalcularPortaParams, CHAPA_TIPOS_PADRAO, CORES, CORES_PADRAO, MODELOS, normalizarParams } from './calculoAcm';
import { calcularConjunto } from './acmConjunto';

export type AcmDoor3DParams = CalcularPortaParams;

/** Uma peça de perfil de alumínio (montante/travessa/batente/verga), como um box 3D. */
export interface AluBox3D {
    id: string;
    grupo: 'folha' | 'marco';
    pos: string;
    peca: string;
    /** Centro da peça em mm, origem no canto inferior-esquerdo do vão (X: largura, Y: altura, Z: profundidade). */
    center: [number, number, number];
    /** Dimensões da caixa (largura, altura, profundidade) em mm. */
    size: [number, number, number];
}

/** Uma chapa ACM (bandeja lado A ou B, peça da cava, fundo de junta, U de borda) como um plano fino. */
export interface SheetPanel3D {
    id: string;
    slot: string;
    lado: 'A' | 'B' | null;
    peca: string;
    corNome: string;
    corHex: string;
    center: [number, number, number];
    size: [number, number, number];
    /** Eixo do afastamento na vista explodida. Padrão 'z' (chapas se separam pra frente/trás). */
    explodeAxis?: 'x' | 'z';
    /** Direção do afastamento (+1, -1 ou nenhum). Se omitido em eixo 'z', é inferido do sinal de center[2]. */
    explodeDir?: 1 | -1 | 0;
}

export interface HardwarePiece3D {
    id: string;
    tipo: 'pivo' | 'dobradica' | 'macaneta' | 'puxador' | 'concha';
    center: [number, number, number];
    /** Caixa envolvente (mm). No cilindro, o comprimento é a medida no eixo e o diâmetro a outra. */
    size: [number, number, number];
    forma?: 'caixa' | 'cilindro';
    eixo?: 'x' | 'y' | 'z';
    /** kit = cor do kit (maçaneta, dobradiça, puxador); oculto = ferragem escondida; recesso = fundo escuro da concha. */
    acabamento: 'kit' | 'oculto' | 'recesso';
}

export interface AcmDoorScene3D {
    /** Vão bruto (parede), em mm. */
    vao: { largura: number; altura: number };
    /** Folha (quadro móvel), em mm. */
    folha: { largura: number; altura: number };
    /** Profundidade do perfil da linha (mm) — espessura "Z" da estrutura. */
    profundidade: number;
    marco: AluBox3D[];
    /** Alisar (U invertido: 2 pernas + travessa) nas duas faces da parede; só quando params.alisar. */
    alisar?: AluBox3D[];
    estrutura: AluBox3D[];
    chapasFrente: SheetPanel3D[];
    chapasVerso: SheetPanel3D[];
    /** U de Revestimento de Borda — encapa as duas bordas laterais (esquerda/direita) da folha. */
    bordasU: SheetPanel3D[];
    ferragens: HardwarePiece3D[];
    temCava: boolean;
    temJunta: boolean;
    /** Porta que corre na frente da parede (correr): plano da face da parede, em mm. Sem valor = porta dentro do vão. */
    zFaceParede?: number;
    /** Conjunto (dupla, bandeira, fixo lateral): onde cada folha/painel está no vão. */
    folhas?: Array<{ id: string; rotulo: string; papel: string; ox: number; oy: number; largura: number; altura: number; espelhada: boolean }>;
}

/** Perfil do alisar em mm: largura escolhida (20 a 70) e 1 cm de espessura. */
export const ALISAR = { larguraMin: 20, larguraMax: 70, larguraPadrao: 70, espessura: 10 };

export function larguraAlisar(v: unknown): number {
    const n = parseInt(String(v), 10);
    if (!Number.isFinite(n)) return ALISAR.larguraPadrao;
    return Math.min(ALISAR.larguraMax, Math.max(ALISAR.larguraMin, n));
}

/**
 * Peças do alisar: pernas verticais até o topo e travessa entre elas (junta reta), em cada face da parede.
 * É uma peça dobrada: nos dois lados a aba da face se liga por uma virada que atravessa a espessura da parede
 * (sem corte na virada), por fora do vão.
 */
export function pecasAlisar(VL: number, VA: number, dd: number, doisLados = true, largura: number = ALISAR.larguraPadrao): AluBox3D[] {
    const w = larguraAlisar(largura);
    const e = ALISAR.espessura;
    const pecas: AluBox3D[] = [];
    (doisLados ? [1, -1] : [1]).forEach(lado => {
        const z = lado * (dd / 2 + e / 2);
        const suf = lado > 0 ? 'A' : 'B';
        pecas.push(
            { id: `alisar-esq-${suf}`, grupo: 'marco', pos: 'AL', peca: 'Alisar — perna esquerda', center: [-w / 2, (VA + w) / 2, z], size: [w, VA + w, e] },
            { id: `alisar-dir-${suf}`, grupo: 'marco', pos: 'AL', peca: 'Alisar — perna direita', center: [VL + w / 2, (VA + w) / 2, z], size: [w, VA + w, e] },
            { id: `alisar-topo-${suf}`, grupo: 'marco', pos: 'AL', peca: 'Alisar — travessa', center: [VL / 2, VA + w / 2, z], size: [VL, w, e] },
        );
    });
    if (doisLados) {
        const prof = dd + 2 * e;
        pecas.push(
            { id: 'alisar-virada-esq', grupo: 'marco', pos: 'AL', peca: 'Alisar — virada esquerda', center: [-e / 2, VA / 2, 0], size: [e, VA, prof] },
            { id: 'alisar-virada-dir', grupo: 'marco', pos: 'AL', peca: 'Alisar — virada direita', center: [VL + e / 2, VA / 2, 0], size: [e, VA, prof] },
            { id: 'alisar-virada-topo', grupo: 'marco', pos: 'AL', peca: 'Alisar — virada superior', center: [VL / 2, VA + e / 2, 0], size: [VL, e, prof] },
        );
    }
    return pecas;
}

export interface CorAlisar { nome: string; hex: string }

/**
 * Cor predominante de um lado da porta: a cor com maior área de revestimento vista de frente.
 * Conta só as peças voltadas para a face (cava: fundo e abas; ripada: ripas e o fundo que sobra entre elas;
 * fundo dos frisos), ignora fresas, paredes/subidas da cava e U de borda.
 * Empate (diferença até 1 cm²): fica a cor que aparece primeiro na porta (chapa 1, a de cima).
 * Sem chapas: null (quem chama usa a cor do marco).
 */
export function corPredominante(chapas: SheetPanel3D[]): CorAlisar | null {
    const areas = new Map<string, { hex: string; area: number; ordem: number }>();
    const somar = (nome: string, hex: string, area: number) => {
        const atual = areas.get(nome);
        if (atual) atual.area += area;
        else areas.set(nome, { hex, area, ordem: areas.size });
    };
    const porId = new Map(chapas.map(p => [p.id, p]));
    chapas.forEach(p => {
        const [sx, sy] = p.size;
        if (/(^|-)cava-[AB]-(parede|subida)-/.test(p.id)) return; // peças de lado da cava: não aparecem de frente
        if (/(^|-)fresa-/.test(p.id) || /(^|-)puxador-ripa-/.test(p.id)) return; // fresa é rasgo na chapa; puxador fica sobre uma ripa da mesma cor
        const area = sx * sy;
        somar(p.corNome, p.corHex, area);
        // A ripa cobre o fundo da ripada: essa área sai da cor do fundo
        const ripa = /^(.*)ripa-([AB].+)-\d+$/.exec(p.id);
        const fundo = ripa && porId.get(`${ripa[1]}chapa-${ripa[2]}`);
        if (fundo) somar(fundo.corNome, fundo.corHex, -area);
    });
    let melhor: { nome: string; hex: string; area: number; ordem: number } | null = null;
    areas.forEach((v, nome) => {
        if (v.area <= 0) return;
        if (!melhor || v.area > melhor.area + 100 || (Math.abs(v.area - melhor.area) <= 100 && v.ordem < melhor.ordem)) melhor = { nome, ...v };
    });
    const m = melhor as { nome: string; hex: string } | null;
    return m ? { nome: m.nome, hex: m.hex } : null;
}

/** Cor do alisar de cada face da parede: a predominante do lado da porta voltado para ela (A = frente, B = verso). */
export function coresAlisar(cena: Pick<AcmDoorScene3D, 'chapasFrente' | 'chapasVerso'>, corMarco: CorAlisar): { A: CorAlisar; B: CorAlisar } {
    return {
        A: corPredominante(cena.chapasFrente) || corMarco,
        B: corPredominante(cena.chapasVerso) || corMarco,
    };
}

const ESP_CHAPA = 3;       // TECBOND ACM 3mm
const FOLGA_CHAPA = 4;     // afastamento visual entre chapa e estrutura, sem sobrepor faces

function hex(slot: string, cores?: Record<string, string>) {
    const nome = cores?.[slot] || CORES_PADRAO[slot] || 'Preto';
    return CORES[nome] || '#333333';
}

function nome(slot: string, cores?: Record<string, string>) {
    return cores?.[slot] || CORES_PADRAO[slot] || 'Preto';
}

/** Extrai as 3 larguras (flange frente · aba · flange verso) do nome do perfil, ex.: "60×47×60" -> [60, 47, 60]. */
function parseUBorda(nomeSecao: string): [number, number, number] {
    const partes = nomeSecao.split(/[×xX]/).map(s => parseFloat(s.trim()));
    if (partes.length === 3 && partes.every(n => !isNaN(n))) return [partes[0], partes[1], partes[2]];
    return [30, 40, 30];
}

// ---- Ferragens à mostra (cor do kit), montadas dos dois lados da folha ----

const LADOS: Array<[string, 1 | -1]> = [['A', 1], ['B', -1]];

/** Maçaneta de alavanca: roseta + haste + alavanca apontando para o centro da folha (sentido +X). */
function macaneta(x: number, y: number, zFace: number): HardwarePiece3D[] {
    return LADOS.flatMap(([lado, s]) => [
        { id: `macaneta-${lado}-roseta`, tipo: 'macaneta', acabamento: 'kit', forma: 'cilindro', eixo: 'z',
          center: [x, y, s * (zFace + 4)], size: [52, 52, 8] },
        { id: `macaneta-${lado}-haste`, tipo: 'macaneta', acabamento: 'kit', forma: 'cilindro', eixo: 'z',
          center: [x, y, s * (zFace + 8 + 22)], size: [18, 18, 44] },
        { id: `macaneta-${lado}-alavanca`, tipo: 'macaneta', acabamento: 'kit',
          center: [x + 58, y, s * (zFace + 8 + 44 - 9)], size: [140, 18, 18] },
        { id: `macaneta-${lado}-espelho`, tipo: 'macaneta', acabamento: 'kit', forma: 'cilindro', eixo: 'z',
          center: [x, y - 85, s * (zFace + 3)], size: [36, 36, 6] },
    ] as HardwarePiece3D[]);
}

/** Puxador sobreposto: barra vertical afastada da folha, presa por dois pés. */
function puxadorSobreposto(x: number, fy: number, FA: number, eixo: number, zFace: number): HardwarePiece3D[] {
    const comp = Math.min(1600, FA * 0.55);
    const yCentro = Math.min(Math.max(comp / 2 + 150, eixo), fy + FA - comp / 2 - 100);
    return LADOS.flatMap(([lado, s]) => [
        { id: `puxador-${lado}-barra`, tipo: 'puxador', acabamento: 'kit', forma: 'cilindro', eixo: 'y',
          center: [x, yCentro, s * (zFace + 55)], size: [32, comp, 32] },
        ...[-1, 1].map(k => ({
            id: `puxador-${lado}-pe${k}`, tipo: 'puxador', acabamento: 'kit', forma: 'cilindro', eixo: 'z',
            center: [x, yCentro + k * (comp / 2 - 120), s * (zFace + 27)], size: [18, 18, 55],
        })),
    ] as HardwarePiece3D[]);
}

/**
 * Constrói o modelo de cena 3D a partir dos mesmos parâmetros aceitos por calcularPorta().
 * Usa como base as mesmas "zonas" (X) e "travessas" (Y) que o motor de desenho 2D usa,
 * apenas dando espessura real (Z) a cada camada.
 */
/** Cena de UMA folha (com o marco, se pedido) já posicionada: `fx`/`fy` são o canto inferior esquerdo dela no vão. */
function cenaDeUmaFolha(d: any, opts: { fx?: number; fy?: number; fixa?: boolean; comMarco: boolean }): AcmDoorScene3D {
    const m = d.modelo;
    const correr = m.id === 'correr';
    const dd: number = m.d;
    const VL = d.entrada.vaoLargura;
    const VA = d.entrada.vaoAltura;
    const FL = d.folha.largura;
    const FA = d.folha.altura;
    const cores = d.entrada.cores;

    // Folha centralizada no vão (aprox.: mesma lógica de folga do svgConjunto).
    const fx = opts.fx ?? (VL - FL) / 2;
    const fy = opts.fy ?? m.folgaInf;

    const toVao = (x: number, y: number): [number, number] => [x + fx, y + fy];

    // ---- Marco (batentes + verga), grupo 'marco' ----
    const marco: AluBox3D[] = [];
    const btL = m.btL, btT = m.btT;
    // Correr: o marco é o cabeçote (trilho) por cima do vão; nos demais, batentes e verga
    const zFrenteCorrer = dd / 2 + FOLGA_CHAPA + ESP_CHAPA;
    const zFaceParede = -(zFrenteCorrer + 15);
    if (!opts.comMarco) {
        // o marco do conjunto é montado à parte
    } else if (correr) {
        const cab = d.cabecote!;
        const x0Cab = cab.abreParaDireita ? -80 : VL + 80 - cab.comprimento;
        const zCabFrente = zFrenteCorrer + 25;
        marco.push({
            id: 'cabecote', grupo: 'marco', pos: 'CB', peca: 'Cabeçote (trilho superior) revestido em ACM',
            center: [x0Cab + cab.comprimento / 2, VA + 20 + cab.altura / 2, (zCabFrente + zFaceParede) / 2],
            size: [cab.comprimento, cab.altura, zCabFrente - zFaceParede]
        });
    } else {
        marco.push({
            id: 'marco-esq', grupo: 'marco', pos: 'B1', peca: 'Batente vertical esquerdo',
            center: [btL / 2, VA / 2, 0], size: [btL, VA, dd]
        });
        marco.push({
            id: 'marco-dir', grupo: 'marco', pos: 'B1', peca: 'Batente vertical direito',
            center: [VL - btL / 2, VA / 2, 0], size: [btL, VA, dd]
        });
        marco.push({
            id: 'marco-verga', grupo: 'marco', pos: 'V1', peca: 'Verga do vão (tubo)',
            center: [VL / 2, VA - btT / 2, 0], size: [VL - 2 * btL, btT, dd]
        });
    }

    // ---- Estrutura da folha: montantes (zonas 'montante'/'junta') ----
    const estrutura: AluBox3D[] = [];
    let seq = 0;
    for (const z of d.zonas) {
        if (z.tipo === 'montante') {
            const [cx] = toVao((z.x0 + z.x1) / 2, 0);
            estrutura.push({
                id: `mont-${seq++}`, grupo: 'folha', pos: 'F1/F2',
                peca: 'Montante da folha',
                center: [cx, fy + FA / 2, 0],
                size: [z.largura, FA, dd]
            });
        }
    }
    // Travessa superior e inferior
    estrutura.push({
        id: 'trav-sup', grupo: 'folha', pos: 'F3', peca: 'Travessa superior',
        center: [fx + FL / 2, fy + FA - dd / 2, 0], size: [FL - 2 * dd, dd, dd]
    });
    estrutura.push({
        id: 'trav-inf', grupo: 'folha', pos: 'F3', peca: 'Travessa inferior',
        center: [fx + FL / 2, fy + dd / 2, 0], size: [FL - 2 * dd, dd, dd]
    });
    // Travessas intermediárias, por campo, nas posições Y calculadas
    const campos = d.zonas.filter((z: any) => z.tipo === 'campo');
    d.travessas.posicoes.forEach((y: number, i: number) => {
        campos.forEach((z: any) => {
            estrutura.push({
                id: `trav-int-${i}-${z.x0}`, grupo: 'folha', pos: 'F4+',
                peca: `Travessa intermediária — ${z.nome || 'campo'}`,
                center: [fx + (z.x0 + z.x1) / 2, fy + FA - (y + dd / 2), 0],
                size: [z.largura, dd, dd]
            });
        });
    });

    // ---- Chapas ACM (bandejas lado A/B) — planos finos à frente e atrás da estrutura ----
    const chapasFrente: SheetPanel3D[] = [];
    const chapasVerso: SheetPanel3D[] = [];
    const zFrente = dd / 2 + FOLGA_CHAPA + ESP_CHAPA / 2;
    const zVerso = -(dd / 2 + FOLGA_CHAPA + ESP_CHAPA / 2);

    d.bandejas.forEach((p: any) => {
        // Cada peça tem a própria cor (partes de divisão e linhas de friso horizontal podem diferir da chapa de origem)
        const slotA = 'A' + p.origem;
        const slotB = 'B' + p.origem;
        const corPeca = (lado: 'A' | 'B') => d.pecasACM.find((x: any) => x.pos === lado + p.rotulo)?.cor;
        const nomeA = corPeca('A') || cores[slotA], nomeB = corPeca('B') || cores[slotB];
        const [cxA] = toVao((p.x0 + p.x1) / 2, 0);
        const cyChapa = fy + FA - (p.y0 + p.y1) / 2;
        const pushLado = (lado: 'A' | 'B', alvo: SheetPanel3D[], zFace: number, slot: string, nomeCor: string) => {
            const tc = d.tiposChapa[lado + p.rotulo];
            const sinal = lado === 'A' ? 1 : -1;
            if (tc.tipo === 'ripada') {
                // Chapa de fundo (lisa) sobre a estrutura; as ripas são coladas nela e avançam "prof" para fora, com vão entre elas
                const nomeFundo = tc.fundo || nomeCor;
                alvo.push({
                    id: `chapa-${lado}${p.rotulo}`, slot, lado,
                    peca: `Fundo da ripada — lado ${lado} (chapa ${p.rotulo})`,
                    corNome: nomeFundo, corHex: CORES[nomeFundo] || '#000',
                    center: [cxA, cyChapa, zFace], size: [p.face, p.altura, ESP_CHAPA]
                });
                const zRipa = sinal * (dd / 2 + FOLGA_CHAPA + ESP_CHAPA + tc.prof / 2);
                (tc.posicoes as number[]).forEach((x0r, k) => alvo.push({
                    id: `ripa-${lado}${p.rotulo}-${k}`, slot, lado,
                    peca: `Ripa ${k + 1} — lado ${lado} (chapa ${p.rotulo})`,
                    corNome: nomeCor, corHex: CORES[nomeCor] || '#000',
                    center: [fx + x0r + tc.ripa / 2, cyChapa, zRipa],
                    size: [tc.ripa, p.altura, tc.prof]
                }));
                // Puxador integrado: um trecho alto (60 de profundidade) sobre a primeira ripa
                const px = d.puxadorRipa;
                if (px && p.origem === 1 && p.linha === 0 && (px.lados as string[]).includes(lado)) {
                    alvo.push({
                        id: `puxador-ripa-${lado}`, slot, lado,
                        peca: `Puxador integrado (ripa alta) — lado ${lado}`,
                        corNome: nomeCor, corHex: CORES[nomeCor] || '#000',
                        center: [fx + px.x0 + px.ripa / 2, fy + FA - (px.yTopo + px.yBase) / 2, sinal * (dd / 2 + FOLGA_CHAPA + ESP_CHAPA + px.profundidade / 2)],
                        size: [px.ripa, px.comprimento, px.profundidade]
                    });
                }
                return;
            }
            alvo.push({
                id: `chapa-${lado}${p.rotulo}`, slot, lado,
                peca: `Bandeja lado ${lado} — ${p.face} de face`,
                corNome: nomeCor, corHex: CORES[nomeCor] || '#000',
                center: [cxA, cyChapa, zFace],
                size: [p.face, p.altura, ESP_CHAPA]
            });
            if (tc.tipo === 'frisada') {
                // Fresas: friso fino e escuro rente à face
                (tc.posicoes as number[]).forEach((v, k) => alvo.push({
                    id: `fresa-${lado}${p.rotulo}-${k}`, slot, lado,
                    peca: `Fresa ${k + 1} — lado ${lado} (chapa ${p.rotulo})`,
                    corNome: 'Preto', corHex: '#1b1b1b',
                    center: tc.direcao === 'vertical' ? [fx + v, cyChapa, zFace + sinal * (ESP_CHAPA / 2 + 0.2)] : [cxA, fy + FA - v, zFace + sinal * (ESP_CHAPA / 2 + 0.2)],
                    size: tc.direcao === 'vertical' ? [CHAPA_TIPOS_PADRAO.frisada.largura, p.altura, 0.4] : [p.face, CHAPA_TIPOS_PADRAO.frisada.largura, 0.4]
                }));
            }
        };
        pushLado('A', chapasFrente, zFrente, slotA, nomeA);
        pushLado('B', chapasVerso, zVerso, slotB, nomeB);
    });

    // ---- Peça 02 (cava do puxador), quando existir ----
    // Modelada com profundidade real: a peça 02 forma um canal em "U" — duas paredes
    // (que recuam da face para dentro do quadro) + um fundo recuado — e não um painel
    // chapado na mesma espessura das outras bandejas (ver svgP02Secao em acmGenerator.ts).
    // Nas pontas (topo/base), o canal não simplesmente termina: a peça faz a "virada" e
    // sobe uma aba plana (comprimento = CAVA.face) que cobre a travessa superior/inferior
    // (ver svgP02Ponta em acmGenerator.ts — extremidade = virada + face).
    const zCava = d.zonas.find((z: any) => z.tipo === 'cava');
    if (zCava && d.cava) {
        const K = d.cava; // calcularPeca02 já embutido em d.cava
        const parede = K.parede;              // profundidade do recuo (parede do canal)
        const face = K.face;                  // comprimento da aba que sobe e cobre a travessa
        // As paredes ficam encostadas exatamente nas bordas da boca da cava (mesma boca das
        // abas), sem recuo lateral — senão sobra uma fresta sem material entre a aba e a
        // parede (o "buraco" — esse vão não existe na peça real).
        const xEsqIn = fx + zCava.x0;
        const xDirIn = fx + zCava.x1;
        // A bandeja anterior (pegador) já avança CAVA.aba (40mm) pra dentro da boca da
        // cava, na mesma profundidade da chapa (ver limites/"pegador" em calculoAcm.ts) —
        // a aba da peça 02 não pode começar antes disso, senão sobrepõe a chapa principal.
        const xFlapEsq = fx + zCava.x0 + K.aba;
        const xFlapDir = fx + zCava.x1;
        const cyCava = fy + FA / 2;
        // O canal reto (sem aba) fica entre as duas abas que cobrem as travessas
        // (fundoComprimento = folhaAltura - 2*face, igual ao calcularPeca02).
        const alturaCava = FA - 2 * face;
        const larguraFundo = (xDirIn - xEsqIn) - 2 * ESP_CHAPA; // encaixa justo entre as duas paredes
        const larguraFlap = xFlapDir - xFlapEsq;

        const pushCavaPocket = (lado: 'A' | 'B', slot: string, target: SheetPanel3D[], zFace: number) => {
            const sinal = zFace >= 0 ? 1 : -1;
            const zFundo = zFace - sinal * parede;
            const corHexV = hex(slot, cores);
            const corNomeV = nome(slot, cores);
            target.push({
                id: `cava-${lado}-parede-esq`, slot, lado,
                peca: `Peça 02 — parede esquerda da cava (lado ${lado})`,
                corNome: corNomeV, corHex: corHexV,
                center: [xEsqIn, cyCava, (zFace + zFundo) / 2],
                size: [ESP_CHAPA, alturaCava, parede]
            });
            target.push({
                id: `cava-${lado}-parede-dir`, slot, lado,
                peca: `Peça 02 — parede direita da cava (lado ${lado})`,
                corNome: corNomeV, corHex: corHexV,
                center: [xDirIn, cyCava, (zFace + zFundo) / 2],
                size: [ESP_CHAPA, alturaCava, parede]
            });
            target.push({
                id: `cava-${lado}-fundo`, slot, lado,
                peca: `Peça 02 — fundo da cava (lado ${lado})`,
                corNome: corNomeV, corHex: corHexV,
                center: [(xEsqIn + xDirIn) / 2, cyCava, zFundo],
                size: [larguraFundo, alturaCava, ESP_CHAPA]
            });
            // Abas superior e inferior: a peça vira e sobe, coplanar com a face da chapa,
            // cobrindo a largura toda da boca da cava sobre a travessa.
            target.push({
                id: `cava-${lado}-aba-sup`, slot, lado,
                peca: `Peça 02 — aba superior (cobre a travessa, lado ${lado})`,
                corNome: corNomeV, corHex: corHexV,
                center: [(xFlapEsq + xFlapDir) / 2, fy + FA - face / 2, zFace],
                size: [larguraFlap, face, ESP_CHAPA]
            });
            target.push({
                id: `cava-${lado}-aba-inf`, slot, lado,
                peca: `Peça 02 — aba inferior (cobre a travessa, lado ${lado})`,
                corNome: corNomeV, corHex: corHexV,
                center: [(xFlapEsq + xFlapDir) / 2, fy + face / 2, zFace],
                size: [larguraFlap, face, ESP_CHAPA]
            });
            // "Subida" — fecha a transição onde o fundo recuado (zFundo) sobe até o nível
            // da aba (zFace), no alto e embaixo do canal. Sem essa peça, o degrau fica
            // aberto e aparece como um buraco entre o canal e a aba.
            const zTampa = (zFace + zFundo) / 2;
            const alturaTampa = Math.abs(zFace - zFundo);
            target.push({
                id: `cava-${lado}-subida-sup`, slot, lado,
                peca: `Peça 02 — subida para a aba superior (lado ${lado})`,
                corNome: corNomeV, corHex: corHexV,
                center: [(xEsqIn + xDirIn) / 2, fy + FA - face, zTampa],
                size: [larguraFundo, ESP_CHAPA, alturaTampa]
            });
            target.push({
                id: `cava-${lado}-subida-inf`, slot, lado,
                peca: `Peça 02 — subida para a aba inferior (lado ${lado})`,
                corNome: corNomeV, corHex: corHexV,
                center: [(xEsqIn + xDirIn) / 2, fy + face, zTampa],
                size: [larguraFundo, ESP_CHAPA, alturaTampa]
            });
        };

        pushCavaPocket('A', 'cavaA', chapasFrente, zFrente);
        pushCavaPocket('B', 'cavaB', chapasVerso, zVerso);
    }

    // ---- Fundo de cada friso: uma peça por lado, colada direto no alumínio, 3 mm abaixo da face da chapa ----
    // (vertical: uma tira na altura toda; horizontal: tiras na largura, interrompidas pela cava)
    (d.frisos as any[]).forEach((f) => {
        const trechos: Array<[number, number]> = f.orientacao === 'vertical' ? [[0, 0]]
            : zCava ? [[4, zCava.x0], [zCava.x1, FL - 4]] : [[4, FL - 4]];
        trechos.forEach(([a, b], k) => {
            const [cx] = toVao(f.orientacao === 'vertical' ? f.centro : (a + b) / 2, 0);
            const cy = f.orientacao === 'vertical' ? fy + FA / 2 : fy + FA - f.centro;
            const tam: [number, number, number] = f.orientacao === 'vertical' ? [f.vaoLivre, FA, ESP_CHAPA] : [b - a, f.vaoLivre, ESP_CHAPA];
            ([['A', chapasFrente, zFrente - ESP_CHAPA], ['B', chapasVerso, zVerso + ESP_CHAPA]] as const).forEach(([lado, alvo, z]) => alvo.push({
                id: `friso-fundo-${lado}-${f.indice}${f.orientacao === 'vertical' ? '' : '-' + k}`, slot: f.slot, lado,
                peca: `Fundo do friso ${f.indice + 1} (${f.orientacao}) — lado ${lado}`,
                corNome: f.cor, corHex: CORES[f.cor] || '#000',
                center: [cx, cy, z],
                size: tam
            }));
        });
    });
    const temFriso = (d.frisos as any[]).length > 0;

    // ---- U de Revestimento de Borda — encapa as duas bordas laterais da folha (pos. P1, qtd 2) ----
    // Perfil em U: flange sobre a face frontal + aba que envolve a borda + flange sobre a face traseira,
    // com as larguras reais do "nome" do perfil (ex.: 60×47×60 na linha Social).
    // As flanges ficam POR BAIXO (atrás) das chapas principais — encostadas na face interna
    // delas, do lado da estrutura — porque a chapa ACM é colada por cima, cobrindo a flange
    // do U. Só a aba lateral (na quina da borda) fica realmente visível por fora.
    const bordasU: SheetPanel3D[] = [];
    const uBorda = MODELOS[d.entrada.modelo].uBorda;
    const [flangeFrenteW, , flangeVersoW] = parseUBorda(uBorda.nome);
    // face externa da chapa frontal/traseira (onde a chapa principal termina)
    const zFrenteChapaOuter = zFrente + ESP_CHAPA / 2;
    const zVersoChapaOuter = zVerso - ESP_CHAPA / 2;
    // a flange fica encostada na face INTERNA da chapa (lado da estrutura), por baixo dela
    const zFrenteFlange = zFrente - ESP_CHAPA;
    const zVersoFlange = zVerso + ESP_CHAPA;
    // a aba lateral (visível na quina) fica rente às faces externas das chapas, sem projetar pra fora
    const zFrenteFlangeOuter = zFrenteChapaOuter;
    const zVersoFlangeOuter = zVersoChapaOuter;
    const corUHex = hex('U', cores);
    const corUNome = nome('U', cores);
    const cyBorda = fy + FA / 2;

    const pushBordaLateral = (ladoNome: string, xBorda: number, entra: 1 | -1) => {
        // O perfil U sai inteiro (as 3 partes juntas) pro lado de fora da porta na vista
        // explodida — não separa as pernas entre si, senão o "U" se desmonta visualmente.
        const foraDir: 1 | -1 = entra === 1 ? -1 : 1;
        bordasU.push({
            id: `uborda-${ladoNome}-frente`, slot: 'U', lado: null,
            peca: `U de Revestimento de Borda — flange frente (${ladoNome})`,
            corNome: corUNome, corHex: corUHex, explodeAxis: 'x', explodeDir: foraDir,
            center: [xBorda + entra * flangeFrenteW / 2, cyBorda, zFrenteFlange],
            size: [flangeFrenteW, FA, ESP_CHAPA]
        });
        bordasU.push({
            id: `uborda-${ladoNome}-verso`, slot: 'U', lado: null,
            peca: `U de Revestimento de Borda — flange verso (${ladoNome})`,
            corNome: corUNome, corHex: corUHex, explodeAxis: 'x', explodeDir: foraDir,
            center: [xBorda + entra * flangeVersoW / 2, cyBorda, zVersoFlange],
            size: [flangeVersoW, FA, ESP_CHAPA]
        });
        bordasU.push({
            id: `uborda-${ladoNome}-aba`, slot: 'U', lado: null,
            peca: `U de Revestimento de Borda — aba lateral (${ladoNome})`,
            corNome: corUNome, corHex: corUHex, explodeAxis: 'x', explodeDir: foraDir,
            center: [xBorda, cyBorda, (zFrenteFlangeOuter + zVersoFlangeOuter) / 2],
            size: [ESP_CHAPA, FA, zFrenteFlangeOuter - zVersoFlangeOuter]
        });
    };
    if (opts.fixa) {
        // painel fixo: as bordas ficam dentro do marco, sem U de borda
    } else if (correr) {
        // Correr: as quatro bordas ficam à vista, então o U reveste laterais, topo e base
        const esp = 2 * (zFrenteCorrer);
        const painelU = (id: string, peca: string, center: [number, number, number], size: [number, number, number], extra: Partial<SheetPanel3D>) => bordasU.push({
            id, slot: 'U', lado: null, peca, corNome: corUNome, corHex: corUHex, center, size, ...extra,
        });
        painelU('uborda-esquerda', 'U de borda (esquerda)', [fx + ESP_CHAPA / 2, cyBorda, 0], [ESP_CHAPA, FA, esp], { explodeAxis: 'x', explodeDir: -1 });
        painelU('uborda-direita', 'U de borda (direita)', [fx + FL - ESP_CHAPA / 2, cyBorda, 0], [ESP_CHAPA, FA, esp], { explodeAxis: 'x', explodeDir: 1 });
        painelU('uborda-topo', 'U de borda (topo)', [fx + FL / 2, fy + FA - ESP_CHAPA / 2, 0], [FL - 2 * ESP_CHAPA, ESP_CHAPA, esp], { explodeDir: 0 });
        painelU('uborda-base', 'U de borda (base)', [fx + FL / 2, fy + ESP_CHAPA / 2, 0], [FL - 2 * ESP_CHAPA, ESP_CHAPA, esp], { explodeDir: 0 });
    } else {
        pushBordaLateral('esquerda', fx, 1);
        pushBordaLateral('direita', fx + FL, -1);
    }

    // ---- Ferragens: pivô/fechadura (esquerda, eixo a FE da base — igual ao 2D) ou dobradiças (lateral direita) ----
    const ferragens: HardwarePiece3D[] = [];
    const zFaceExterna = zFrenteChapaOuter;
    if (opts.fixa) {
        // painel fixo: sem ferragem
    } else if (correr) {
        // Puxador concha embutido perto da borda que fecha (a folha abre para o lado do cabeçote) e roldanas escondidas no cabeçote
        const abreDir = d.cabecote!.abreParaDireita;
        const xConcha = abreDir ? fx + 90 : fx + FL - 90;
        const yConcha = Math.min(Math.max(d.entrada.fechaduraEixo || 1000, 300), fy + FA - 300);
        LADOS.forEach(([lado, sg]) => ferragens.push(
            { id: `concha-${lado}-moldura`, tipo: 'concha', acabamento: 'kit', center: [xConcha, yConcha, sg * (zFaceExterna + 2)], size: [46, 240, 4] },
            { id: `concha-${lado}-fundo`, tipo: 'concha', acabamento: 'recesso', center: [xConcha, yConcha + 20, sg * (zFaceExterna + 4.3)], size: [30, 170, 0.6] },
            { id: `concha-${lado}-cilindro`, tipo: 'concha', acabamento: 'kit', forma: 'cilindro', eixo: 'z', center: [xConcha, yConcha - 85, sg * (zFaceExterna + 4.5)], size: [14, 14, 1] },
        ));
        [fx + 150, fx + FL - 150].forEach((x, i) => ferragens.push({
            id: `roldana-${i}`, tipo: 'pivo', acabamento: 'oculto', forma: 'cilindro', eixo: 'z', center: [x, fy + FA + 30, 0], size: [50, 50, 20],
        }));
    } else if (MODELOS[d.entrada.modelo].ferragem === 'pivo') {
        ferragens.push({
            id: 'pivo', tipo: 'pivo', acabamento: 'oculto',
            center: [fx + 26 / 2, fy + d.entrada.fechaduraEixo, 0],
            size: [26, 80, dd + 20]
        });
        // Sem cava: puxador sobreposto (barra vertical na cor do kit), dos dois lados da folha
        if (!zCava) ferragens.push(...puxadorSobreposto(fx + 110, fy, FA, d.entrada.fechaduraEixo, zFaceExterna));
    } else {
        const dob = d.resumo.ferragem.quantidade;
        for (let i = 0; i < dob; i++) {
            const y = 150 + i * ((FA - 300) / Math.max(1, dob - 1));
            // Nó da dobradiça: aparece na fresta entre a folha e o batente, do lado A
            ferragens.push({
                id: `dobradica-${i}`, tipo: 'dobradica', acabamento: 'kit', forma: 'cilindro', eixo: 'y',
                center: [fx + FL + 2, fy + y, zFaceExterna - 5],
                size: [14, 100, 14]
            });
        }
        // Maçaneta do lado oposto às dobradiças, na altura do eixo da fechadura
        ferragens.push(...macaneta(fx + 60, Math.min(Math.max(d.entrada.fechaduraEixo, 300), fy + FA - 200), zFaceExterna));
    }

    return {
        vao: { largura: VL, altura: VA },
        folha: { largura: FL, altura: FA },
        profundidade: dd,
        marco,
        estrutura,
        chapasFrente,
        chapasVerso,
        bordasU,
        ferragens,
        temCava: !!zCava && !!d.cava,
        temJunta: temFriso,
        ...(correr ? { zFaceParede } : {})
    };
}

/** Espelha as peças de uma folha em X em torno do centro dela (folha com puxador do outro lado). */
function espelharFolha3D(c: AcmDoorScene3D, cx: number): void {
    const x = (v: [number, number, number]): [number, number, number] => [2 * cx - v[0], v[1], v[2]];
    c.estrutura.forEach(b => { b.center = x(b.center); });
    c.ferragens.forEach(h => { h.center = x(h.center); });
    [...c.chapasFrente, ...c.chapasVerso, ...c.bordasU].forEach(pn => {
        pn.center = x(pn.center);
        if (pn.explodeAxis === 'x' && pn.explodeDir) pn.explodeDir = (-pn.explodeDir) as 1 | -1;
    });
}

const PREFIXO_FOLHA: Record<string, string> = { f1: '', f2: 'f2-', fixo: 'fx-' };

/**
 * Constrói o modelo de cena 3D a partir dos mesmos parâmetros aceitos por calcularPorta().
 * Uma porta simples é uma folha com o marco; dupla, bandeira e fixo lateral montam cada folha/painel na sua posição
 * (as peças das folhas além da primeira levam o prefixo `f2-` / `fx-` no id) e o marco do conjunto por cima.
 */
export function construirCena3D(paramsEntrada: AcmDoor3DParams): AcmDoorScene3D {
    const params = normalizarParams(paramsEntrada); // links antigos ainda trazem `tipo: 'ripada' | 'correr'`
    const conj = calcularConjunto(params);
    const principal = conj.folhas[0];
    const m = principal.d.modelo;
    if (conj.variacao === 'simples') {
        const cena = cenaDeUmaFolha(principal.d, { comMarco: true });
        if (principal.espelhada && m.id !== 'correr') espelharFolha3D(cena, (cena.vao.largura) / 2);
        return { ...cena, ...(params.alisar ? { alisar: pecasAlisar(conj.vao.largura, conj.vao.altura, m.d, true, params.alisarMm) } : {}) };
    }
    const dd: number = m.d;
    const VL = conj.vao.largura, VA = conj.vao.altura;
    const cena: AcmDoorScene3D = {
        vao: { largura: VL, altura: VA }, folha: { largura: principal.largura, altura: principal.altura }, profundidade: dd,
        marco: [], estrutura: [], chapasFrente: [], chapasVerso: [], bordasU: [], ferragens: [], temCava: false, temJunta: false,
        folhas: conj.folhas.map(f => ({ id: f.id, rotulo: f.rotulo, papel: f.papel, ox: f.ox, oy: f.oy, largura: f.largura, altura: f.altura, espelhada: f.espelhada })),
    };
    conj.folhas.forEach(f => {
        const parte = cenaDeUmaFolha(f.d, { fx: f.ox, fy: f.oy, fixa: f.papel === 'fixa', comMarco: false });
        if (f.espelhada) espelharFolha3D(parte, f.ox + f.largura / 2);
        const pre = PREFIXO_FOLHA[f.id];
        const marcar = <T extends { id: string }>(itens: T[]) => itens.forEach(i => { i.id = pre + i.id; });
        marcar(parte.estrutura); marcar(parte.chapasFrente); marcar(parte.chapasVerso); marcar(parte.bordasU); marcar(parte.ferragens);
        cena.estrutura.push(...parte.estrutura); cena.chapasFrente.push(...parte.chapasFrente); cena.chapasVerso.push(...parte.chapasVerso);
        cena.bordasU.push(...parte.bordasU); cena.ferragens.push(...parte.ferragens);
        cena.temCava = cena.temCava || parte.temCava;
        cena.temJunta = cena.temJunta || parte.temJunta;
    });
    // Marco do conjunto: batentes, verga e, conforme a variação, a travessa sob a bandeira ou a trave central
    const btL = m.btL, btT = m.btT;
    cena.marco.push(
        { id: 'marco-esq', grupo: 'marco', pos: 'B1', peca: 'Batente vertical esquerdo', center: [btL / 2, VA / 2, 0], size: [btL, VA, dd] },
        { id: 'marco-dir', grupo: 'marco', pos: 'B1', peca: 'Batente vertical direito', center: [VL - btL / 2, VA / 2, 0], size: [btL, VA, dd] },
        { id: 'marco-verga', grupo: 'marco', pos: 'V1', peca: 'Verga do vão (tubo)', center: [VL / 2, VA - btT / 2, 0], size: [VL - 2 * btL, btT, dd] },
    );
    if (conj.marco?.travessa) {
        const { y0, y1 } = conj.marco.travessa;
        cena.marco.push({ id: 'marco-travessa', grupo: 'marco', pos: 'T1', peca: 'Travessa do marco (sob a bandeira)', center: [VL / 2, (y0 + y1) / 2, 0], size: [VL - 2 * btL, y1 - y0, dd] });
    }
    if (conj.marco?.trave) {
        const { x0, x1 } = conj.marco.trave;
        cena.marco.push({ id: 'marco-trave', grupo: 'marco', pos: 'M1', peca: 'Trave central do marco', center: [(x0 + x1) / 2, (VA - btT) / 2, 0], size: [x1 - x0, VA - btT, dd] });
    }
    if (params.alisar) cena.alisar = pecasAlisar(VL, VA, dd, true, params.alisarMm);
    return cena;
}
